import { useDebounce, useMediaQuery } from "@uidotdev/usehooks";
import { Fragment, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MeetingView } from "../components/meetings/MeetingView";
import { DayGroups, RecordRow } from "../components/records/RecordList";
import { GetTheApps } from "../components/shell/GetTheApps";
import {
  BackLink,
  Btn,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  SearchField,
  Tabs,
  ui,
} from "../components/ui";
import { isProcessing, useMeetingsList } from "../hooks/useMeetings";
import { colors, fonts } from "../style";
import {
  captureFor,
  formatShortDate,
  formatSpokenDuration,
  formatTime,
  groupByDay,
  PROGRESS_COPY,
  STATUS,
} from "../utils/meetingFormat";
import { Highlighted, useMeetingSearch } from "../utils/search";

const FILTERS = [
  { value: "all", label: "All" },
  { value: "in_person", label: "In person" },
  { value: "online_call", label: "Online calls" },
];

// Below this the selected meeting replaces the list rather than sitting beside it
const STACKED_QUERY = "(max-width: 1100px)";

const style = {
  page: {
    flex: 1,
    display: "flex",
    minHeight: 0,
    fontFamily: fonts.system,
    color: colors.textStrong,
  },
  // Email-style: a narrow list, the meeting takes the rest. Stacked, the
  // list is alone and fills the width.
  list: (stacked) => ({
    flex: stacked ? "1 1 100%" : "none",
    width: stacked ? undefined : "clamp(340px, 34%, 420px)",
    boxSizing: "border-box",
    padding: "28px 16px 24px 24px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  }),
  panel: {
    flex: "1 1 0",
    minWidth: 0,
    boxSizing: "border-box",
    background: colors.sidebar,
    borderLeft: `1px solid ${colors.border}`,
  },
};

function Meetings() {
  const [params] = useSearchParams();
  const selected = params.get("meeting");
  const stacked = useMediaQuery(STACKED_QUERY);
  const list = useMeetingsList();
  const [filter, setFilter] = useState("all");
  const [text, setText] = useState("");
  const debounced = useDebounce(text, 250);
  const algolia = useMeetingSearch(debounced, { hitsPerPage: 30 });
  const searching = text.trim() !== "";
  // Without a meetings search key, filter the loaded meetings instead
  const useAlgolia = searching && algolia.available;

  const linkTo = (sqid) => {
    const next = new URLSearchParams(params);
    next.set("meeting", sqid);
    return `?${next}`;
  };

  const groups = useMemo(() => {
    const needle = debounced.trim().toLowerCase();
    const shown = list.meetings.filter(
      (m) =>
        (filter === "all" || m.capture === filter) &&
        (!needle ||
          `${m.title ?? ""} ${m.summary ?? ""}`.toLowerCase().includes(needle)),
    );
    return groupByDay(shown, (m) => m.recorded_at);
  }, [list.meetings, filter, debounced]);

  if (!list.isLoading && !list.isError && list.total === 0) {
    return (
      <div
        style={{
          ...style.page,
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 16px",
        }}
      >
        <Card
          variant="panel"
          style={{
            padding: "40px 28px",
            maxWidth: "460px",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <GetTheApps />
        </Card>
      </div>
    );
  }

  const showList = !stacked || !selected;
  const showPanel = !stacked || !!selected;

  return (
    <div style={{ ...style.page, flexWrap: stacked ? "wrap" : "nowrap" }}>
      {showList && (
        <section aria-label="Meetings" style={style.list(stacked)}>
          <PageHeader
            title="Meetings"
            actions={
              list.total > 0 && (
                <span style={{ ...ui.muted, fontSize: "13px" }}>
                  {list.total} {list.total === 1 ? "meeting" : "meetings"}
                </span>
              )
            }
          />

          <SearchField
            label="Search meetings"
            placeholder={
              algolia.available
                ? "Search titles, notes and what was said"
                : "Search meeting titles and summaries"
            }
            value={text}
            onChange={(e) => setText(e.target.value)}
          />

          {/* Search hits don't say how they were recorded, so filters only apply to the list */}
          {!useAlgolia && (
            <Tabs
              variant="pills"
              ariaLabel="Filter"
              value={filter}
              onChange={setFilter}
              items={FILTERS}
            />
          )}

          {useAlgolia ? (
            <SearchHits search={algolia} selected={selected} linkTo={linkTo} />
          ) : (
            <MeetingGroups
              list={list}
              groups={groups}
              selected={selected}
              linkTo={linkTo}
              empty={
                searching
                  ? "No meetings match that search."
                  : filter === "all"
                    ? "No meetings yet."
                    : `No ${filter === "in_person" ? "in-person meetings" : "online calls"} yet.`
              }
            />
          )}
        </section>
      )}

      {showPanel && (
        <aside
          aria-label="Meeting"
          style={{
            ...style.panel,
            ...(stacked
              ? { flexBasis: "100%", borderLeft: 0 }
              : {
                  position: "sticky",
                  top: 0,
                  alignSelf: "flex-start",
                  height: "100vh",
                  overflowY: "auto",
                }),
          }}
        >
          {selected ? (
            <>
              {stacked && (
                <div style={{ padding: "16px 24px 0" }}>
                  <BackLink to="/meetings">Meetings</BackLink>
                </div>
              )}
              <MeetingView key={selected} sqid={selected} variant="panel" />
            </>
          ) : (
            <EmptyState
              icon="meetings"
              style={{
                height: "100%",
                minHeight: "300px",
                justifyContent: "center",
              }}
            >
              Pick a meeting to see its summary, transcript and notes.
            </EmptyState>
          )}
        </aside>
      )}
    </div>
  );
}

function MeetingGroups({ list, groups, selected, linkTo, empty }) {
  if (list.isLoading) {
    return <LoadingState />;
  }
  if (list.isError) {
    return (
      <ErrorState onRetry={() => list.refetch()} retryLabel="Try again">
        Couldn't load your meetings.
      </ErrorState>
    );
  }

  return (
    <>
      {groups.length === 0 ? (
        <EmptyState inline>{empty}</EmptyState>
      ) : (
        <DayGroups
          groups={groups}
          renderItem={(m) => (
            <MeetingRow
              key={m.sqid}
              meeting={m}
              active={m.sqid === selected}
              to={linkTo(m.sqid)}
            />
          )}
        />
      )}
      {list.hasNextPage && (
        <Btn
          style={{ alignSelf: "center" }}
          loading={list.isFetchingNextPage}
          onClick={() => list.fetchNextPage()}
        >
          Show older meetings
        </Btn>
      )}
    </>
  );
}

function MeetingRow({ meeting, active, to }) {
  const status = meeting.status.value;
  const ready = status === "PROCESSED";
  const capture = captureFor(meeting.capture);
  const detail = ready
    ? meeting.summary ||
      (meeting.has_transcription ? "No summary" : "No speech detected")
    : PROGRESS_COPY[status];

  return (
    <RecordRow
      to={to}
      active={active}
      dot={{
        color: ready ? capture.color : (STATUS[status]?.color ?? colors.amber),
        title: ready ? capture.label : STATUS[status]?.label,
        pulse: isProcessing(meeting),
      }}
      title={meeting.title || "Untitled meeting"}
      meta={formatTime(meeting.recorded_at)}
      detail={
        detail &&
        dotJoin([
          // Length only once known; busy rows lead with their status
          ready && formatSpokenDuration(meeting.duration_seconds),
          ready && meeting.capture === "online_call" && "Online call",
          detail,
        ])
      }
      detailColor={ready ? colors.textMuted : colors.amberText}
      sentiment={ready ? meeting.sentiment : null}
    />
  );
}

// The parts that are there (text or nodes) separated by " · ", else null
function dotJoin(parts) {
  const shown = parts.filter(Boolean);
  if (!shown.length) return null;
  return shown.map((part, i) => (
    <Fragment key={i}>
      {i > 0 && " · "}
      {part}
    </Fragment>
  ));
}

// "12 min · snippet", leaving out whichever is missing
const hitDetail = (hit) =>
  dotJoin([
    formatSpokenDuration(hit.durationSeconds),
    hit.snippet && <Highlighted value={hit.snippet} />,
  ]);

function SearchHits({ search, selected, linkTo }) {
  if (search.isLoading || (search.isFetching && !search.data)) {
    return <LoadingState />;
  }
  if (search.isError) {
    return (
      <ErrorState onRetry={() => search.refetch()} retryLabel="Try again">
        Search failed.
      </ErrorState>
    );
  }
  const hits = search.data?.hits ?? [];
  if (!hits.length) {
    return <EmptyState inline>No meetings match that search.</EmptyState>;
  }
  return (
    <DayGroups
      style={{ paddingTop: "8px" }}
      groups={[{ key: "hits", items: hits }]}
      renderItem={(hit) => (
        <RecordRow
          key={hit.sqid}
          to={linkTo(hit.sqid)}
          active={hit.sqid === selected}
          title={<Highlighted value={hit.title} />}
          meta={hit.recordedAt && formatShortDate(hit.recordedAt)}
          detail={hitDetail(hit)}
        />
      )}
    />
  );
}

export default Meetings;
